import {
  useEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { deriveEstimateSettingsDefaults } from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { CustomerApprovalMethod } from '@/types/estimate';

interface UseEstimateSettingsDefaultsArgs {
  estimateSettings: unknown;
  isEditMode: boolean;
  duplicateId?: string;
  billingDepositPercent?: number;
  setNotes: Dispatch<SetStateAction<string>>;
  setDepositValue: Dispatch<SetStateAction<number>>;
  setDepositType: Dispatch<SetStateAction<'$' | '%'>>;
  setDepositRequired: Dispatch<SetStateAction<boolean>>;
  setRequireSignature: Dispatch<SetStateAction<boolean>>;
  setCustomerApprovalMethod: Dispatch<SetStateAction<CustomerApprovalMethod | ''>>;
  setValidUntil: Dispatch<SetStateAction<string>>;
  /**
   * Fields an applied estimate template has already authored.
   *
   * A REF, not a value, and read at effect time on purpose. Both sides here
   * wait on their own query, so whichever resolves last used to win: a template
   * requiring a 50% deposit or turning signatures off would be flipped back to
   * the company defaults whenever settings happened to land second. Reading the
   * ref when the effect fires makes the template win in BOTH orders — if it has
   * not loaded yet the set is empty, settings apply, and the template overwrites
   * them when it arrives.
   */
  templateAuthoredFieldsRef?: MutableRefObject<Set<string>>;
}

export function useEstimateSettingsDefaults({
  estimateSettings,
  isEditMode,
  duplicateId,
  billingDepositPercent,
  setNotes,
  setDepositValue,
  setDepositType,
  setDepositRequired,
  setRequireSignature,
  setCustomerApprovalMethod,
  setValidUntil,
  templateAuthoredFieldsRef,
}: UseEstimateSettingsDefaultsArgs) {
  const didApplyEstimateSettingsRef = useRef(false);
  // Tracks whether estimate settings set an explicit deposit so the billing
  // fallback effect knows not to override it.
  const estimateSettingsSetDepositRef = useRef(false);
  const didApplyBillingDepositRef = useRef(false);

  // Apply estimate settings defaults (notes, approval, valid-until, deposit from estimate config)
  useEffect(() => {
    if (!estimateSettings || isEditMode || duplicateId || didApplyEstimateSettingsRef.current)
      return;

    didApplyEstimateSettingsRef.current = true;
    const defaults = deriveEstimateSettingsDefaults(estimateSettings);
    const authored = templateAuthoredFieldsRef?.current;
    const templateOwns = (field: string) => authored?.has(field) ?? false;

    if (defaults?.notes && !templateOwns('notes')) setNotes(defaults.notes);
    if (defaults?.requireSignature !== undefined && !templateOwns('requireSignature')) {
      setRequireSignature(defaults.requireSignature);
    }
    if (defaults?.customerApprovalMethod)
      setCustomerApprovalMethod(defaults.customerApprovalMethod);
    if (defaults?.validUntil && !templateOwns('validUntil')) setValidUntil(defaults.validUntil);

    if (defaults?.depositValue !== undefined) {
      estimateSettingsSetDepositRef.current = true;
      if (!templateOwns('depositValue')) setDepositValue(defaults.depositValue);
      if (defaults.depositType && !templateOwns('depositType')) {
        setDepositType(defaults.depositType);
      }
    } else if (defaults?.depositType && !templateOwns('depositType')) {
      setDepositType(defaults.depositType);
    }
  }, [
    estimateSettings,
    isEditMode,
    duplicateId,
    setNotes,
    setDepositValue,
    setDepositType,
    setRequireSignature,
    setCustomerApprovalMethod,
    setValidUntil,
  ]);

  // Apply billing deposit default as fallback — only if estimate settings
  // didn't already set an explicit deposit value.
  useEffect(() => {
    if (
      isEditMode ||
      duplicateId ||
      didApplyBillingDepositRef.current ||
      billingDepositPercent === undefined ||
      billingDepositPercent <= 0 ||
      estimateSettingsSetDepositRef.current
    )
      return;

    didApplyBillingDepositRef.current = true;
    // A template that states a deposit policy outranks the company fallback,
    // whichever query happened to resolve first.
    const authored = templateAuthoredFieldsRef?.current;
    if (authored?.has('depositValue') || authored?.has('depositRequired')) return;
    setDepositValue(billingDepositPercent);
    setDepositType('%');
    setDepositRequired(true);
  }, [
    billingDepositPercent,
    isEditMode,
    duplicateId,
    setDepositValue,
    setDepositType,
    setDepositRequired,
  ]);
}
